import {PersonaSize} from 'components/FluentUICompat';

import React, {useMemo} from 'react';

import {
	Regulation,
	RegulatoryDocument,
	Requirement,
	RequirementFilterOptions,
	RequirementStatus,
} from 'types';
import {
	InfinityListColumn,
	InfinityListColumnFilterOptions,
	InfinityListColumnMetaData,
} from 'components/InfinityList';
import {CellWithEntityLink} from 'components/EntityList/CellWithEntityLink';
import {
	renderArrayField,
	renderArrayFieldWithTooltipLink,
	renderAttachments,
	renderDate,
	renderFaqs,
	renderPersona,
	renderReferenceField,
	renderRequirementCategory,
	renderRequirementStatus,
	renderRichtext,
} from 'components/EntityList/ColumnRenderers';

import {createHrefFromRequirement} from './requirementLinks.utils';
import {useRequirementsTranslation} from './hooks/useRequirementsTranslation';
import {
	getOptionsSorted,
	queryStringMongoArraySTD,
	queryStringMongoDateSTD,
	queryStringMongoEnumSTD,
} from 'components/InfinityList/InfinitListStandardHooks';
import {useTranslation} from 'react-i18next';
import {GetKeywordSortOptionsQuery} from 'features/AdminSection/hooks/keywords.generated';
import {GetMarketSortOptionsQuery} from 'features/AdminSection/hooks/markets.generated';
import useQuestionsPermissions from 'components/Faq/hooks/useQuestionsPermissions';

export type IRequirementsPageFilterOptions = {
	(
		dataQuery: RequirementFilterOptions | undefined,
		keywordData?: GetKeywordSortOptionsQuery | undefined,
		marketData?: GetMarketSortOptionsQuery | undefined,
	): InfinityListColumnFilterOptions[];
};

export const useRequirementsPageColumn = () => {
	const {t} = useRequirementsTranslation();
	const {shouldShowFaq} = useQuestionsPermissions();
	const widthSTD150 = {minWidth: 150, maxWidth: 200};
	const resizeMulti = {isResizable: true, isMultiline: true};

	const filterArraySimple = {
		filterOnFilter: true,
		filterType: 'Array',
	};
	const filterArray = {
		...filterArraySimple,
		onRender: renderArrayField(),
	};

	const filterArray150 = {...widthSTD150, ...resizeMulti, ...filterArray};

	const columns: InfinityListColumn[] = useMemo(() => {
		const tempColumns: InfinityListColumn[] = [
			{
				key: 'attachments',
				name: t('Attachments'),
				fieldName: 'attachments',
				minWidth: 20,
				maxWidth: 20,
				onRender: renderAttachments(),
				isIconOnly: true,
				iconName: 'Attach',
			},
			{
				key: 'name',
				name: t('Name'),
				fieldName: 'name',
				minWidth: 200,
				maxWidth: 250,
				sortable: true,
				...resizeMulti,
				...filterArraySimple,
				onRender(item: Requirement) {
					return (
						<CellWithEntityLink href={createHrefFromRequirement(item)}>
							{item.status === RequirementStatus.Interim ||
							item.status === RequirementStatus.InterimCompleted ? (
								<div style={{color: 'gray'}}>
									<i>{item.name}</i>
								</div>
							) : (
								item.name
							)}
						</CellWithEntityLink>
					);
				},
			},
			{
				key: 'requirementId',
				name: t('RequirementId'),
				fieldName: 'requirementId',
				minWidth: 75,
				maxWidth: 75,
				sortable: true,
				...filterArraySimple,
				onRender: (item: Requirement) => (
					<CellWithEntityLink href={`/requirements/${item.id}`}>
						{item.requirementId}
					</CellWithEntityLink>
				),
			},
			{
				key: 'version',
				name: t('Version'),
				fieldName: 'version',
				minWidth: 70,
				maxWidth: 70,
				...filterArraySimple,
			},
			{
				key: 'regulations',
				name: t('Regulations'),
				fieldName: 'regulations',
				...filterArray150,
				onRender: renderArrayFieldWithTooltipLink<Regulation>(
					reg => reg.regulationNumber,
					reg => `/regulations/${reg.id}`,
				),
			},
			{
				key: 'regulatoryDocuments',
				name: t('RegulatoryDocuments'),
				fieldName: 'regulatoryDocuments',
				...filterArray150,
				onRender: renderArrayFieldWithTooltipLink<RegulatoryDocument>(
					doc => doc.name,
					doc => `/regulatoryDocuments/${doc.id}`,
				),
			},
			{
				key: 'markets',
				name: t('Markets'),
				fieldName: 'markets',
				...filterArray150,
			},
			{
				key: 'vexClusters',
				name: t('VexCluster'),
				fieldName: 'vexClusters',
				...filterArray150,
			},
			{
				key: 'dateEffective',
				name: t('DateEffective'),
				fieldName: 'dateEffective',
				...widthSTD150,
				sortable: true,
				filterType: 'Date',
				onRender: renderDate(),
			},
			{
				key: 'dateExpiration',
				name: t('DateExpiration'),
				fieldName: 'dateExpiration',
				...widthSTD150,
				sortable: true,
				filterType: 'Date',
				onRender: renderDate(),
			},
			{
				key: 'status',
				name: t('Status'),
				fieldName: 'status',
				minWidth: 70,
				maxWidth: 70,
				sortable: true,
				...filterArraySimple,
				onRender: renderRequirementStatus(),
			},
			{
				key: 'definition',
				name: t('Definition'),
				fieldName: 'definition',
				minWidth: 400,
				maxWidth: 550,
				...resizeMulti,
				onRender: renderRichtext(),
			},
			{
				key: 'systemLevels',
				name: t('SystemLevels'),
				fieldName: 'systemLevels',
				...filterArray150,
			},
			{
				key: 'keywords',
				name: t('Keywords'),
				fieldName: 'keywords',
				...filterArray150,
			},
			{
				key: 'tags',
				name: t('Tags'),
				fieldName: 'tags',
				...filterArray150,
			},
			{
				key: 'createdBy',
				name: t('CreatedBy'),
				fieldName: 'createdBy',
				...widthSTD150,
				...resizeMulti,
				...filterArraySimple,
				onRender: renderPersona({size: PersonaSize.size8}),
			},
			{
				key: 'modifiedAt',
				name: t('ModifiedAt'),
				fieldName: 'modifiedAt',
				...widthSTD150,
				...resizeMulti,
				sortable: true,

				filterType: 'Date',
				onRender: renderDate(),
			},
			{
				key: 'category',
				name: t('Category'),
				fieldName: 'category',
				...widthSTD150,
				...filterArraySimple,
				onRender: renderRequirementCategory(),
			},
			{
				key: 'references',
				name: t('References'),
				fieldName: 'references',
				minWidth: 300,
				isIconOnly: true,
				iconName: 'Link',
				isResizable: true,
				isMultiline: true,
				onRender: renderReferenceField(),
			},
		];

		if (shouldShowFaq) {
			tempColumns.push({
				key: 'FAQ',
				name: 'Q&As',
				fieldName: 'faq',
				minWidth: 100,
				isResizable: true,
				onRender: renderFaqs(),
			});
		}

		return tempColumns;
	}, [shouldShowFaq]);

	return columns;
};

export const useRequirementColumnMetaData = () => {
	const columnMetaData: InfinityListColumnMetaData[] = [
		{
			key: 'name',
			mongoDBFn: queryStringMongoArraySTD('_id', true),
			linkName: 'id',
		},
		{
			key: 'vexClusters',
			mongoDBFn: queryStringMongoArraySTD('vexClusterRefs._id'),
		},

		{
			key: 'dateEffective',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'dateExpiration',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
		{
			key: 'regulations',
			mongoDBFn: queryStringMongoArraySTD(
				'requirementParagraphRefs.regulationRef._id',
			),
		},
		{
			key: 'regulatoryDocuments',
			mongoDBFn: queryStringMongoArraySTD(
				'requirementParagraphRefs.regdocRef._id',
			),
		},
		{
			key: 'systemLevels',
			mongoDBFn: queryStringMongoArraySTD('systemLevelRefs._id'),
		},
		{
			key: 'markets',
			mongoDBFn: queryStringMongoArraySTD('marketRefs._id'),
		},
		{
			key: 'keywords',
			mongoDBFn: queryStringMongoArraySTD('keywordRefs._id'),
		},
		{
			key: 'tags',
			mongoDBFn: queryStringMongoArraySTD('tagRefs._id'),
		},
		{
			key: 'createdBy',
			mongoDBFn: queryStringMongoArraySTD('createdById'),
		},
		{
			key: 'requirementId',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'version',
			mongoDBFn: queryStringMongoArraySTD(),
		},
		{
			key: 'status',
			mongoDBFn: queryStringMongoEnumSTD(),
		},
		{
			key: 'category',
			mongoDBFn: queryStringMongoEnumSTD(),
		},
		{
			key: 'modifiedAt',
			mongoDBFn: queryStringMongoDateSTD(),
			sortInverted: true,
		},
	];
	return columnMetaData;
};

export const useRequirementsPageFilterOptions: IRequirementsPageFilterOptions =
	(requirementFilterOptions, keywordData, marketData) => {
		const {t: tStatus} = useTranslation('common/enums', {
			keyPrefix: 'RequirementStatus',
		});

		const {t: tCategory} = useTranslation('common/enums', {
			keyPrefix: 'RequirementCategory',
		});

		const columnFilterOptions: InfinityListColumnFilterOptions[] =
			useMemo(() => {
				const queryData = requirementFilterOptions;

				const marketsAll = marketData?.markets ?? [];
				const keywordsAll = keywordData?.keywords ?? [];

				const marketOptions = queryData?.marketOptions ?? [];
				const keywordOptions = queryData?.keywordOptions ?? [];

				const marketOptionsSorted = getOptionsSorted(marketsAll, marketOptions);

				const keywordOptionsSorted = getOptionsSorted(
					keywordsAll,
					keywordOptions,
				);

				const filterOptions = [
					{
						key: 'name',
						fieldValuesUnique: queryData?.requirementOptions,
					},
					{
						key: 'vexClusters',
						fieldValuesUnique: queryData?.vexClusterOptions ?? [],
					},
					{
						key: 'requirementId',
						fieldValuesUnique: queryData?.requirementIdOptions ?? [],
					},
					{
						key: 'version',
						fieldValuesUnique: queryData?.versionOptions ?? [],
					},
					{
						key: 'regulations',
						fieldValuesUnique: queryData?.regOptions ?? [],
					},
					{
						key: 'regulatoryDocuments',
						fieldValuesUnique: queryData?.regDocOptions ?? [],
					},
					{
						key: 'status',
						fieldValuesUnique: (queryData?.statusOptions ?? []).map(x => {
							return {optionKey: x.optionKey, name: tStatus(x.name)};
						}),
					},
					{
						key: 'systemLevels',
						fieldValuesUnique: queryData?.systemLevelOptions ?? [],
					},
					{
						key: 'markets',
						fieldValuesUnique: marketOptionsSorted,
					},
					{
						key: 'keywords',
						fieldValuesUnique: keywordOptionsSorted,
					},
					{
						key: 'tags',
						fieldValuesUnique: queryData?.tagOptions,
					},
					{
						key: 'createdBy',
						fieldValuesUnique: queryData?.createdByOptions ?? [],
					},
					{
						key: 'category',
						fieldValuesUnique: (queryData?.categoryOptions ?? []).map(x => {
							return {optionKey: x.optionKey, name: tCategory(x.name)};
						}),
					},
				];

				return filterOptions.map(x => {
					return {...x, fieldValuesUnique: x.fieldValuesUnique ?? []};
				});
			}, [requirementFilterOptions, keywordData, marketData]);
		return columnFilterOptions;
	};
